import { Action, Reducer } from 'redux';
import {
  AzureFilesState,
  AzureFilesUnloadedState,
  AzureRegisteredFilesEntry,
  AzureStorageFilesEntry,
} from '../states/AzureFilesState';
import * as Actions from '../actions';

type KnownAction = Actions.AzureFileUpload.AzureFileKnownAction;

const emptyEntry: AzureRegisteredFilesEntry = {
  isLoading: false,
  files: [],
  error: undefined,
  loadedAt: undefined,
};

const emptyStorageEntry: AzureStorageFilesEntry = {
  isLoading: false,
  files: [],
  error: undefined,
  loadedAt: undefined,
};

export const AzureFilesReducer: Reducer<AzureFilesState> = (
  state: AzureFilesState | undefined,
  incomingAction: Action
): AzureFilesState => {
  if (state === undefined) {
    return AzureFilesUnloadedState;
  }

  const action = incomingAction as KnownAction;
  const current = (key: string): AzureRegisteredFilesEntry => state.byTarget[key] ?? emptyEntry;
  const currentStorage = (key: string): AzureStorageFilesEntry => state.storageByTarget?.[key] ?? emptyStorageEntry;

  switch (action.type) {
    case 'AZUREFILE_GETREGISTERED_REQUEST_ACTION':
      return {
        ...state,
        byTarget: {
          ...state.byTarget,
          [action.targetKey]: { ...current(action.targetKey), isLoading: true, error: undefined },
        },
      };
    case 'AZUREFILE_GETREGISTERED_RECEIVE_ACTION':
      return {
        ...state,
        byTarget: {
          ...state.byTarget,
          [action.targetKey]: {
            isLoading: false,
            files: action.files,
            error: undefined,
            loadedAt: new Date().toISOString(),
          },
        },
      };
    case 'AZUREFILE_GETREGISTERED_FAIL_ACTION':
      return {
        ...state,
        byTarget: {
          ...state.byTarget,
          [action.targetKey]: { ...current(action.targetKey), isLoading: false, error: action.error },
        },
      };
    case 'AZUREFILE_GETSTORAGE_REQUEST_ACTION':
      return {
        ...state,
        storageByTarget: {
          ...(state.storageByTarget ?? {}),
          [action.targetKey]: { ...currentStorage(action.targetKey), isLoading: true, error: undefined },
        },
      };
    case 'AZUREFILE_GETSTORAGE_RECEIVE_ACTION':
      return {
        ...state,
        storageByTarget: {
          ...(state.storageByTarget ?? {}),
          [action.targetKey]: {
            isLoading: false,
            files: action.files,
            error: undefined,
            loadedAt: new Date().toISOString(),
          },
        },
      };
    case 'AZUREFILE_GETSTORAGE_FAIL_ACTION':
      return {
        ...state,
        storageByTarget: {
          ...(state.storageByTarget ?? {}),
          [action.targetKey]: { ...currentStorage(action.targetKey), isLoading: false, error: action.error },
        },
      };
    case 'AZUREFILE_REGISTER_SUCCESS_ACTION':
    case 'AZUREFILE_LINK_SUCCESS_ACTION': {
      // The owner service answered with the record: show it right away (the list is refreshed afterwards).
      const file = action.file;
      if (!file) return state;
      const entry = current(action.targetKey);
      return {
        ...state,
        byTarget: {
          ...state.byTarget,
          [action.targetKey]: {
            ...entry,
            files: [file, ...entry.files.filter((item) => item.id !== file.id)],
          },
        },
      };
    }
    case 'AZUREFILE_UNLINK_SUCCESS_ACTION': {
      const entry = state.byTarget[action.targetKey];
      if (!entry) return state;
      return {
        ...state,
        byTarget: {
          ...state.byTarget,
          [action.targetKey]: { ...entry, files: entry.files.filter((item) => item.id !== action.fileId) },
        },
      };
    }
    case 'AZUREFILE_PROTOCOLSPONSOR_REQUEST_ACTION':
      return {
        ...state,
        protocolSponsors: { ...(state.protocolSponsors ?? {}), [action.protocolId]: { status: 'loading' } },
      };
    case 'AZUREFILE_PROTOCOLSPONSOR_RECEIVE_ACTION':
      return {
        ...state,
        protocolSponsors: {
          ...(state.protocolSponsors ?? {}),
          [action.protocolId]: { status: 'loaded', sponsorId: action.sponsorId },
        },
      };
    case 'AZUREFILE_PROTOCOLSPONSOR_FAIL_ACTION':
      return {
        ...state,
        protocolSponsors: { ...(state.protocolSponsors ?? {}), [action.protocolId]: { status: 'failed' } },
      };
    default:
      return state;
  }
};
